import { useMemo, useRef } from "react";

import { useDebouncedSave, useUserContext } from "utils";

import { useFieldImportPersist } from "pages/app/scenario-analysis/hooks/useFieldImportPersist";
import { cropYearHasAnyEntry } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { toApiCropYear } from "pages/app/scenario-analysis/utils/scenarioApiMappers";
import { useScenarioOnboarding } from "pages/app/scenario-onboarding/ScenarioOnboardingContext";
import { useGetSoilSightOnboardingSetting, useUpdateSoilSightOnboardingSetting } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingSetting";
import { isDraftForFarm, mergeInProgressDraft } from "pages/app/scenario-onboarding/utils/onboardingState";

const AUTOSAVE_DEBOUNCE_MS = 800;

/**
 * Step-1 draft autosave: the baseline rotation must survive an abandon at ANY point, not just
 * after the CTA, so edits are debounced through the same persist path the CTA uses. Failures are
 * silent — the CTA persists authoritatively.
 */
export const useBaselineDraftAutosave = () => {
  const { user } = useUserContext();
  const { onboardingState, isLoading: isOnboardingSettingLoading } = useGetSoilSightOnboardingSetting();
  const {
    cropYears,
    selectedFarmId,
    selectedFieldId,
    importMeta,
    scenarioRefs,
    calcStatus,
    restoreStatus,
    setScenarioRefs,
    draftSaveQueueRef,
    persistedProjectIdRef,
  } = useScenarioOnboarding();

  const { persistFieldImport, refreshAfterImport } = useFieldImportPersist();
  const updateOnboardingSetting = useUpdateSoilSightOnboardingSetting();

  // Latest selected farm, so a save that outlives a farm switch can tell it is stale.
  const selectedFarmIdRef = useRef(selectedFarmId);
  selectedFarmIdRef.current = selectedFarmId;

  const draft = useMemo(() => ({ cropYears, selectedFarmId, selectedFieldId }), [cropYears, selectedFarmId, selectedFieldId]);

  useDebouncedSave({
    value: draft,
    delay: AUTOSAVE_DEBOUNCE_MS,
    initial: "debounce",
    // Any change to what the guard reads restarts the debounce, so the guard holds for the whole window.
    rearmOn: [restoreStatus, calcStatus, isOnboardingSettingLoading, onboardingState, user?.farm?.id, user?.farm?.soilSightProjectId, scenarioRefs.projectId],
    shouldSkip: (value) => {
      const hasAnyEntry = value.cropYears.some(cropYearHasAnyEntry);
      // An all-empty rotation is only a savable draft state when the on-screen rows are known
      // to reflect the server: a completed restore ("done", user deleted restored crops) or a
      // rotation this session persisted itself (scenarioRefs.projectId set by a prior save).
      // Blank scaffold rows with neither behind them must never persist — e.g. the farm's
      // project holds a rotation but the draft key was overwritten by another farm's draft,
      // so no restore runs; a blank save here would silently wipe that project's baseline.
      const canSaveEmptyRotation = restoreStatus === "done" || Boolean(scenarioRefs.projectId);
      // A restorable draft must be fully restored before ANY save: the selector auto-selects
      // farm/field while the restore still waits on its queries ("idle"), and saving the blank
      // scaffold in that window would overwrite the persisted rotation the restore is about to
      // read. "error" stays blocked too — the rows on screen may not reflect the server state.
      const isRestorePending =
        isOnboardingSettingLoading ||
        (isDraftForFarm(onboardingState, user?.farm?.id) &&
          Boolean(user?.farm?.soilSightProjectId) &&
          (restoreStatus === "idle" || restoreStatus === "restoring" || restoreStatus === "error"));
      // Selecting a farm switches the platform-active farm; until that lands, every farm-scoped
      // lookup here still answers for the previous farm.
      const isFarmSwitchPending = value.selectedFarmId !== user?.farm?.id;
      // Wait for a field, something to save, and a settled flow (no restore/calculation in flight).
      return (
        !value.selectedFarmId ||
        !value.selectedFieldId ||
        isFarmSwitchPending ||
        (!hasAnyEntry && !canSaveEmptyRotation) ||
        isRestorePending ||
        calcStatus === "calculating"
      );
    },
    // Compared when the queued save starts, since an earlier queued save may already have stored this state.
    serialize: (value) =>
      JSON.stringify({ selectedFieldId: value.selectedFieldId, years: value.cropYears.map((year, index) => toApiCropYear(year, index + 1)) }),
    // Shared with the CTA, which drains it before persisting, so the two can never create a project each.
    queueRef: draftSaveQueueRef,
    // Leaving step 1 right after an edit keeps it, except during a CTA submit, which saves the same rows itself.
    flushOnUnmount: () => calcStatus !== "calculating",
    save: async (value) => {
      const { cropYears: years, selectedFarmId: farmId, selectedFieldId: fieldId } = value;
      // The switch cleared this farm's project refs; a stale save must not write them back.
      const isFarmSwitched = () => selectedFarmIdRef.current !== farmId;
      if (isFarmSwitched() || !farmId || !fieldId) return false;
      try {
        // In-progress key BEFORE any project mutation (grandfathering disambiguation) —
        // only written once; later autosaves keep the existing state (import meta etc.).
        // Any saved step-1 action also marks the landing as passed (later entries skip it).
        const isDraftAlreadyStamped =
          isDraftForFarm(onboardingState, farmId) && onboardingState?.status === "in-progress" && onboardingState.landingSeen === true;
        if (!isDraftAlreadyStamped) {
          await updateOnboardingSetting.mutateAsync(
            mergeInProgressDraft(onboardingState, {
              farmId,
              importedFieldId: importMeta?.importedFieldId,
              importBannerDismissed: importMeta?.bannerDismissed,
              landingSeen: true,
            })
          );
          if (isFarmSwitched()) return false;
        }

        // Ref first (scenarioRefs lags a fresh create); soilSightProjectId is the active farm's only.
        const activeFarmProjectId = user?.farm?.id === farmId ? user?.farm?.soilSightProjectId ?? null : null;
        const resolvedProjectId = persistedProjectIdRef.current ?? scenarioRefs.projectId ?? activeFarmProjectId;
        const hadProject = Boolean(resolvedProjectId);
        const persistResult = await persistFieldImport({
          klimFarmId: farmId,
          fieldId,
          cropYears: years,
          // After the first save the reference scenario is known — later saves skip its GET.
          knownReferenceScenarioId: scenarioRefs.baselineScenarioId,
          getTargetProjectId: () => resolvedProjectId,
        });
        if (isFarmSwitched()) return false;

        persistedProjectIdRef.current = persistResult.soilSightProjectId;
        setScenarioRefs((previous) => ({
          ...previous,
          projectId: persistResult.soilSightProjectId,
          baselineScenarioId: previous.baselineScenarioId ?? persistResult.referenceScenarioId,
        }));
        if (!hadProject) {
          // Refresh user.farm.soilSightProjectId so a later re-entry finds the draft project.
          await refreshAfterImport({ soilSightProjectId: persistResult.soilSightProjectId, invalidateScenarios: false });
        }
        return true;
      } catch {
        // Autosave is best-effort; the CTA persists authoritatively and reports errors.
        return false;
      }
    },
  });
};
